import {BookOpen, CheckCircle, Landmark} from 'lucide-react'

const GUIDE_STEPS = [
  {
    title: "Assess Your Pasture Quality",
    content:
      "Start by evaluating your forage type and condition. Native grasses, improved pastures, and irrigated fields each have vastly different carrying capacities. A well-managed bermudagrass pasture can support 2-3 times more cattle per acre than degraded rangeland.",
  },
  {
    title: "Factor in Annual Rainfall",
    content:
      "Rainfall is the single biggest driver of forage production. Arid regions (under 15 inches/year) may need 15-25+ acres per cow, while areas with 35+ inches of rain can sustain cattle on as few as 2-3 acres per head. Always plan for drought years, not average years.",
  },
  {
    title: "Calculate Animal Unit Months (AUMs)",
    content:
      "An Animal Unit Month (AUM) is the amount of forage needed to feed one 1,000 lb cow with a calf for one month (approximately 780 lbs of dry matter). Divide your total available AUMs by the number of grazing months to determine maximum head count.",
  },
  {
    title: "Adjust for Cattle Size & Season",
    content:
      "Larger breeds consume proportionally more forage. A 1,400 lb cow equals 1.4 Animal Units. Also consider your grazing season length — year-round grazing requires more acres per head than seasonal grazing with supplemental hay.",
  },
  {
    title: "Apply a Safety Margin",
    content:
      "Conservative ranchers stock at 70-80% of maximum capacity to account for drought, seasonal variation, and pasture recovery. Rotational grazing can increase effective carrying capacity by 25-30% compared to continuous grazing.",
  },
]

const KEY_FACTS = [
  "The national average is 2-5 acres per cow-calf pair in the eastern US",
  "Western rangeland may require 10-50+ acres per cow depending on region",
  "Rotational grazing can increase carrying capacity by 25-30%",
  "Overgrazing reduces root depth and long-term pasture productivity",
  "1 AUM = 780 lbs of dry forage matter (26 lbs/day for 30 days)",
  "Soil testing helps optimize fertilization for maximum forage yield",
  "Rest periods of 30-60 days between grazing improve forage recovery",
]

const REGIONAL_DATA = [
  { region: "Southeast US", acres: "2-4", rainfall: "45-60 in" },
  { region: "Midwest / Great Plains", acres: "4-10", rainfall: "25-40 in" },
  { region: "Northern Plains", acres: "8-20", rainfall: "15-25 in" },
  { region: "Southwest / Arid", acres: "20-50+", rainfall: "<15 in" },
  { region: "Pacific Northwest", acres: "2-5", rainfall: "35-60 in" },
  { region: "Irrigated Pasture", acres: "1-2", rainfall: "N/A" },
]

export default function CattlePerAcreGuide() {
  return (
    <section id="stocking-guide" className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14">
          <span className="inline-block text-[#40916C] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Expert Resource
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#283618]">
            How to Calculate Cattle Per Acre
          </h2>
          <p className="mt-3 text-[#606C38] text-base sm:text-lg max-w-2xl mx-auto">
            A comprehensive guide to determining the optimal stocking rate, understanding Animal Unit Months, and managing your pasture for long-term productivity.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Steps */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-6">
              <BookOpen className="w-5 h-5 text-[#2D6A4F]" />
              <h3 className="font-bold text-lg text-[#283618]" style={{ fontFamily: "'Merriweather', serif" }}>
                Step-by-Step Stocking Rate Guide
              </h3>
            </div>

            <div className="space-y-0">
              {GUIDE_STEPS.map((step, index) => (
                <div key={step.title} className="relative flex gap-4">
                  {/* Timeline */}
                  <div className="flex flex-col items-center">
                    <div className="w-9 h-9 rounded-full bg-[#2D6A4F] text-[#FEFAE0] flex items-center justify-center font-bold text-sm shrink-0 z-10">
                      {index + 1}
                    </div>
                    {index < GUIDE_STEPS.length - 1 && (
                      <div className="w-0.5 flex-1 bg-[#B7E4C7] my-1" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="pb-8">
                    <h4 className="font-bold text-[#283618] text-base mb-1.5">{step.title}</h4>
                    <p className="text-sm sm:text-base text-[#606C38] leading-relaxed">{step.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-6">
            {/* Key Facts */}
            <div className="bg-[#FEFAE0] rounded-2xl border border-[#B7E4C7]/40 p-6 shadow-sm sticky top-24">
              <h3 className="font-bold text-lg text-[#283618] mb-4" style={{ fontFamily: "'Merriweather', serif" }}>
                Key Stocking Rate Facts
              </h3>
              <ul className="space-y-3">
                {KEY_FACTS.map((fact) => (
                  <li key={fact} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-[#40916C] shrink-0 mt-0.5" />
                    <span className="text-sm text-[#606C38] leading-relaxed">{fact}</span>
                  </li>
                ))}
              </ul>

              {/* Regional Table */}
              <div className="mt-6 pt-5 border-t border-[#B7E4C7]/30">
                <h4 className="font-semibold text-sm text-[#283618] mb-3 flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-[#2D6A4F]" />
                  Regional Stocking Rates
                </h4>
                <div className="space-y-2">
                  {REGIONAL_DATA.map((item) => (
                    <div key={item.region} className="flex items-center justify-between text-sm">
                      <span className="text-[#283618] font-medium">{item.region}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-[#606C38] text-xs">{item.rainfall}</span>
                        <span className="text-[#2D6A4F] font-bold font-mono text-xs w-14 text-right">{item.acres} ac</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[10px] text-[#606C38]/60 leading-relaxed">
                  Acres per cow-calf pair. Actual rates vary by soil, management, and year. Source: USDA NRCS guidelines.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
